import { Notification as NotificationType } from 'Generated/schema';
import { Box, Text } from 'pounceV2';
import { useTheme } from '@mui/material';
import React from 'react';
import { logError } from 'Helpers/errors';
import { GroupedNotifications } from './types';
import { NotificationPreferences } from './graphql/NotificationPreferences.generated';
import { originalPreferences } from './constants';

const TABLE_NAME_MAPPINGS = {
  AMAZON_EKS_AUDIT: 'Amazon EKS Audit',
  AWS_VPCFLOW: 'VPC Flow',
  AWS_VPCDNS: 'VPC DNS',
  AWS_CLOUDTRAIL: 'CloudTrail',
  AWS_S3SERVERACCESS: 'S3 Server Access',
  AWS_GUARDDUTY: 'GuardDuty',
  NOTION_AUDITLOGS: 'Notion Audit',
};

export const beautifyTableName = (tableName: string): string => {
  const parts = tableName.split('.');
  const tableNameOnly = parts[parts.length - 1];
  const isPantherLogs = parts.some(part => part.toLowerCase() === 'panther_logs');
  if (TABLE_NAME_MAPPINGS[tableNameOnly.toUpperCase()]) {
    const prettyName = TABLE_NAME_MAPPINGS[tableNameOnly.toUpperCase()];
    return isPantherLogs ? `${prettyName} log table` : `${prettyName} table`;
  }
  return isPantherLogs ? `${tableNameOnly} log table` : `${tableName} table`;
};

interface ErrorInfo {
  error: string;
  suggestedFix?: string;
}

export const beautifyErrorMessage = (error: string): ErrorInfo => {
  const errorMessage = error.trim();
  const failedQuerySave = errorMessage.match(
    /failed to save query "([^"]+)": 002003.*SQL compilation error:\s*Object '([^']+)' does not exist or not authorized/im,
  );
  if (failedQuerySave) {
    const [, queryName, tableName] = failedQuerySave;
    const prettyTableName = beautifyTableName(tableName);
    return {
      error: `Failed to save query ${queryName}. The ${prettyTableName} does not exist or is not accessible`,
      suggestedFix:
        'Create missing tables by adding the appropriate log sources. Then, disable and re-enable the pack',
    };
  }
  return { error: errorMessage };
};

interface FormatMessageProps {
  message: string;
  excerpt?: boolean;
}

export const FormatMessage: React.FC<FormatMessageProps> = ({ message, excerpt = false }) => {
  const {
    palette: { tokens },
  } = useTheme();
  const headingColor = tokens.content.text.neutral[2];
  const bodyColor = tokens.content.text.neutral[3];

  const colonIndex = message.indexOf(':');
  if (colonIndex === -1) {
    if (excerpt && message.length > 400) {
      return <>{`${message.substring(0, 400)}...`}</>;
    }
    return <>{message}</>;
  }

  const mainMessage = message.substring(0, colonIndex);
  const errorDetails = message.substring(colonIndex + 1).trim();

  if (
    !mainMessage.includes('Error updating pack') &&
    !mainMessage.includes('Error updating Pack')
  ) {
    if (excerpt && message.length > 400) {
      return <>{`${message.substring(0, 400)}...`}</>;
    }
    return <>{message}</>;
  }

  if (errorDetails.includes(';')) {
    const errors = errorDetails
      .split(';')
      .filter(error => error.trim())
      .map(error => beautifyErrorMessage(error));

    const uniqueFixes = [...new Set(errors.map(e => e.suggestedFix).filter(Boolean))];

    const displayErrors = excerpt ? errors.slice(0, 2) : errors;
    const remainingErrors = excerpt ? Math.max(0, errors.length - 2) : 0;

    return (
      <Box>
        <Text color={headingColor} mb={2} fontWeight="medium">
          {mainMessage}:
        </Text>
        <Box ml={4}>
          {displayErrors.map(({ error }, index) => (
            <Text key={index} color={bodyColor} display="flex" alignItems="flex-start" mb={2}>
              <Text as="span" mr={2}>
                •
              </Text>
              <Text as="span">{error}</Text>
            </Text>
          ))}
          {remainingErrors > 0 && (
            <Text color={bodyColor} display="flex" alignItems="flex-start" mb={2}>
              <Text as="span" mr={2}>
                •
              </Text>
              <Text as="span">
                Check your notifications to see {remainingErrors} other error
                {remainingErrors > 1 ? 's' : ''}
              </Text>
            </Text>
          )}
        </Box>
        {uniqueFixes.length > 0 && (
          <>
            <Text color={headingColor} mt={3} mb={2} fontWeight="medium">
              Suggested fix:
            </Text>
            <Box ml={4}>
              {uniqueFixes.map((fix, index) => (
                <Text key={index} color={bodyColor} display="flex" alignItems="flex-start" mb={2}>
                  <Text as="span" mr={2}>
                    •
                  </Text>
                  <Text as="span">{fix}</Text>
                </Text>
              ))}
            </Box>
          </>
        )}
      </Box>
    );
  }

  const { error, suggestedFix } = beautifyErrorMessage(errorDetails);

  return (
    <Box>
      <Text color={headingColor} mb={2} fontWeight="medium">
        {mainMessage}:
      </Text>
      <Text color={bodyColor} ml={4}>
        {error}
      </Text>
      {suggestedFix && (
        <>
          <Text color={headingColor} mt={3} mb={2} fontWeight="medium">
            Suggested fix:
          </Text>
          <Text color={bodyColor} ml={4}>
            {suggestedFix}
          </Text>
        </>
      )}
    </Box>
  );
};

// Function to get new notifications in the last N seconds
export const getNewNotifications = (
  notifications: NotificationType[],
  seconds = 10,
): NotificationType[] => {
  const currentTime = new Date();
  const thresholdTime = new Date(currentTime.getTime() - seconds * 1000);

  return notifications.filter(notification => {
    const createdAtTime = new Date(notification.createdAt);
    return createdAtTime > thresholdTime;
  });
};

// Function to group notifications by date
export const groupedNotifications = (notifications: NotificationType[]): GroupedNotifications =>
  notifications.reduce((result, notification) => {
    const createdAt = new Date(notification.createdAt);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);

    let dateKey = '';

    if (createdAt >= today) {
      dateKey = 'Today';
    } else if (createdAt >= yesterday) {
      dateKey = 'Yesterday';
    } else {
      dateKey = createdAt.toDateString();
    }

    if (!result[dateKey]) {
      // eslint-disable-next-line no-param-reassign
      result[dateKey] = [];
    }

    result[dateKey].push(notification);
    return result;
  }, {});

// Function to merge notifications preferences
export const mergeNotificationPreferences = (
  fromServer: NotificationPreferences['notificationPreferences'],
): NotificationPreferences['notificationPreferences'] => {
  const mergedPreferences = { preferences: [] };
  // use of closure to keep the original preferences
  const original = originalPreferences;
  // return early if there is no preferences from server
  if (!fromServer) {
    return original;
  }

  original.preferences.forEach((originalPref, index) => {
    const serverPref = fromServer.preferences[index];

    if (serverPref) {
      const mergedChannels = originalPref.channels.map((channel, channelIndex) => {
        const serverChannel = serverPref.channels[channelIndex];
        return { ...channel, ...(serverChannel || {}) };
      });

      mergedPreferences.preferences.push({
        ...originalPref,
        channels: mergedChannels,
      });
    } else {
      mergedPreferences.preferences.push(originalPref);
    }
  });

  return mergedPreferences;
};

class PackUpdateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PackUpdateError';
  }
}

export const logNotification = (title: string, message: string) => {
  // Extract pack name if it's a pack update error
  const colonIndex = message.indexOf(':');
  const mainMessage = message.substring(0, colonIndex);
  const packName = mainMessage.match(/Error updating pack\s*([^:]+)/i)?.[1]?.trim();
  if (!packName) {
    // We currently only want to log pack update errors
    return;
  }

  const error = new PackUpdateError(message);
  logError(error, {
    fingerprint: ['notification', title, packName].filter(Boolean),
    extras: {
      title,
      message,
      packName,
    },
  });
};
